import React from 'react';
import { ViewMedium, Viewport, ViewSmall } from '@shared-ui/viewport-context';
import { EGDSLayoutFlex, EGDSLayoutFlexItem } from '@egds/react-core/layout-flex';
import { EGDSLayoutColumns } from '@egds/react-core/layout-columns';
import { RenderVac, VirtualAgentControlComponent } from './virtual-agent-control';
import { VacLoaderComponent } from 'components/VirtualAgentControl/VacLoaderComponent';
import { VacErrorComponent } from 'components/VirtualAgentControl/VacErrorComponent';
import { IntentButton } from 'components/IntentButtons';
import { EVENTS } from 'utils/logger-events';
import { ClientLogger } from 'bernie-client';
import { getHelpCenterConfig } from 'common/__generated__/api/types';
import { floatingVacProps } from 'components/VirtualAgentControl/virtual-agent-control-types';
import { EGDSHeading } from '@egds/react-core/text';

const logger = ClientLogger.getLoggerWithIdentifier('helpcenter');

type VaContainerProps = {
  data: getHelpCenterConfig.helpCenterPersonalizedView;
  userHasTrip?: boolean;
  signedInUser?: boolean;
};

export const VaContainer: React.FC<VaContainerProps> = ({ data, userHasTrip, signedInUser }: VaContainerProps) => {
  const [renderVac, setRenderVac] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const isIntentButtonsEnabled = !isLoading && !error;
  const { intentButtons, vaLayoutVariant, intentButtonsHeading } = data;

  React.useEffect(() => {
    if (error) {
      removeLoader();
      setRenderVac(false);
    } else {
      setRenderVac(true);
      setIsLoading(true);
    }
  }, [error]);

  const reloadVac = () => {
    logger.logEvent(EVENTS.VAC_RELOADING, { message: 'Reloading VAC' });
    window.location.reload();
    return false;
  };

  const removeLoader = () => {
    setIsLoading(false);
  };

  if (vaLayoutVariant === 2) {
    return (
      <>
        {renderVac && (
          <RenderVac
            config={floatingVacProps}
            virtualAgentProps={{ isLoading, setError, removeLoader }}
            blankSlateLayout
            userHasTrip={userHasTrip}
            signedInUser={signedInUser}
          />
        )}
      </>
    );
  }

  if (vaLayoutVariant === 1) {
    return (
      <div role="region" aria-label="Quick actions">
        {intentButtons && (
          <>
            <EGDSHeading tag="h3" align="left" size={5} className={`button-heading`}>
              {intentButtonsHeading}
            </EGDSHeading>
            <Viewport>
              <ViewSmall>
                <EGDSLayoutColumns columns={1} space={'three'}>
                  <IntentButton enabled={isIntentButtonsEnabled} data={data} />
                </EGDSLayoutColumns>
              </ViewSmall>
              <ViewMedium>
                <EGDSLayoutColumns columns={2} space={'three'}>
                  <IntentButton enabled={isIntentButtonsEnabled} data={data} />
                </EGDSLayoutColumns>
              </ViewMedium>
            </Viewport>
          </>
        )}
        <RenderVac config={floatingVacProps} virtualAgentProps={{ isLoading, setError, removeLoader }} />
      </div>
    );
  }

  return (
    <>
      <Viewport>
        <ViewSmall>
          <EGDSLayoutFlex space="two" wrap="wrap">
            <EGDSLayoutFlexItem grow={1} className="flex-basis-auto">
              <div data-stid="vacContainer">
                {renderVac && (
                  <VirtualAgentControlComponent isLoading={isLoading} removeLoader={removeLoader} setError={setError} />
                )}
                <VacLoaderComponent isLoading={isLoading} />
                <VacErrorComponent error={error} reloadVac={reloadVac} />
              </div>
            </EGDSLayoutFlexItem>
            <EGDSLayoutFlexItem className="flex-basis-auto">
              <div className="buttons-container">
                {intentButtons && (
                  <>
                    <EGDSHeading tag="h3" align="left" size={5} className={`button-heading`}>
                      {intentButtonsHeading}
                    </EGDSHeading>
                    <IntentButton enabled={isIntentButtonsEnabled} data={data} />
                  </>
                )}
              </div>
            </EGDSLayoutFlexItem>
          </EGDSLayoutFlex>
        </ViewSmall>
        <ViewMedium>
          <EGDSLayoutFlex space="two" wrap="wrap">
            <EGDSLayoutFlexItem grow={1} className="flex-basis-auto">
              <div>
                <div
                  role="region"
                  aria-label="Virtual agent"
                  id="vac-container"
                  className={`hide-vac-${isLoading}`}
                  data-stid="vacContainer"
                />
                {renderVac && (
                  <VirtualAgentControlComponent isLoading={isLoading} removeLoader={removeLoader} setError={setError} />
                )}
                <VacLoaderComponent isLoading={isLoading} />
                <VacErrorComponent error={error} reloadVac={reloadVac} />
              </div>
            </EGDSLayoutFlexItem>
            {intentButtons !== null && (
              <>
                <EGDSLayoutFlexItem className="flex-basis-auto">
                  <div className={'gap-between'} />
                </EGDSLayoutFlexItem>
                <EGDSLayoutFlexItem className="flex-basis-auto">
                  <div className="buttons-container">
                    {intentButtons && (
                      <>
                        <EGDSHeading tag="h3" align="left" size={5} className={`button-heading`}>
                          {intentButtonsHeading}
                        </EGDSHeading>
                        <IntentButton enabled={isIntentButtonsEnabled} data={data} />
                      </>
                    )}
                  </div>
                </EGDSLayoutFlexItem>
              </>
            )}
          </EGDSLayoutFlex>
        </ViewMedium>
      </Viewport>
    </>
  );
};
